import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Table, Badge, Button, Form, Spinner, Modal } from "react-bootstrap";
import { useNavigate, useParams } from 'react-router-dom';
import AuthUtility from "../../util/AuthUtility";
import { BrandDetailEntity } from "../../dto/brand/BrandDetailEntity";
import { BrandMediaEntity } from "../../dto/brand/BrandMediaEntity";
import { onBrandAttributeCreate, onBrandDetail, onBrandMediaDelete, onBrandMediaUpload, onRemoveAttributeChoiceFromBrand } from "../../controller/BrandController";
import toast, { Toaster } from 'react-hot-toast';
import { BrandAttributeChoiceEntity } from "../../dto/brand/BrandAttributeChoiceEntity";
import { AttributeEntity } from "../../dto/attribute/AttributeEntity";
import { AttributeChoiceEntity } from "../../dto/attribute/AttributeChoiceEntity";
import { onAttributeChoiceList, onAttributeList } from "../../controller/CategoryController";
import Select from "react-select";
import { Delete } from "@mui/icons-material";
import { BrandEntity } from "../../dto/brand/BrandEntity";
import { onBrandBasicUpdate, onBrandDelete } from "../../controller/BrandController";

type BrandParams = {
    brandId: string;
};

interface ChoiceOption {
  value: number;
  label: string;
}

// ================== COMPONENT ==================
const BrandDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const { brandId } = useParams<BrandParams>();
    const [brandDetail, setBrandDetail] = useState<BrandDetailEntity>({});

    const [showMediaModal, setShowMediaModal] = useState(false);
    const [editingMedia, setEditingMedia] = useState(false);
    const [showAttributeModal, setShowAttributeModal] = useState(false);

    const [newAttribute, setNewAttribute] = useState<BrandAttributeChoiceEntity | undefined>({});
    const [attributeList, setAttributeList] = useState<AttributeEntity[]>([]);
    const [choiceList, setChoiceList] = useState<AttributeChoiceEntity[]>([]);

    const [attributeSearch, setAttributeSearch] = useState("");

    const [newMedia, setNewMedia] = useState<BrandMediaEntity>({
      brandId: parseInt(brandId!),
      role: "ICON",
      status: "ACTIVE",
    });
    const [uploading, setUploading] = useState(false);

    const choiceOptions: ChoiceOption[] = choiceList.map(c => ({
      value: c.id!,
      label: c.valueText || c.status || `Choice ${c.id}`,
    }));


    useEffect(() => {
        if (!AuthUtility.isUserLoggedIn()) {
            navigate("/login");
            window.location.reload();
        } else {
            brandDetailGet();
        }
    }, []);

    const brandDetailGet = async () => {
      const response = await onBrandDetail(parseInt(brandId!));
      const data = response.data;
      if (data !== undefined) {
        setBrandDetail({
            brand: data.brand
                ? {
                    ...data.brand,
                    createdAt: data.brand.createdAt
                    ? new Date(data.brand.createdAt)
                    : undefined,
                    updatedAt: data.brand.updatedAt
                    ? new Date(data.brand.updatedAt)
                    : undefined,
                }
                : undefined,
        
            medias: data.medias?.map((m) => ({
                ...m,
                preview: 'https://ns9.checkin.mn/v1/file/api/files/' + m.fileUuid,
                createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
                updatedAt: m.updatedAt ? new Date(m.updatedAt) : undefined,
            })),
        
            choices: data.choices
        });

        setBasicInfo({
          name: data.brand?.name || "",
          sortOrder: data.brand?.sortOrder || 0,
          status: data.brand?.status || "",
        });
      }
    }
    
      // ================= ADD MEDIA HANDLER =================
    const handleSaveMedia = async () => {
      const formData = new FormData();

      if (editingMedia) {
        // ===== UPDATE MODE =====
        formData.append(
          "brandMedia",
          new Blob([JSON.stringify(newMedia)], {
            type: "application/json"
          })
        );
      } else {
        // ===== ADD MODE =====
        if (!newMedia.data) {
          alert("File is required");
          return;
        }

        const mediaPayload = {
          ...newMedia,
          brandId: parseInt(brandId!), // ensure categoryId is present
        };

        formData.append(
          "brandMedia",
          new Blob([JSON.stringify(mediaPayload)], {
            type: "application/json"
          })
        );

        formData.append("data", newMedia.data);
      }

      try {
        setUploading(true);

        // TODO: replace with your real API
        const res = await onBrandMediaUpload(formData);
        if (res.status == 0) {
          toast.success(res.msg);
        } else {
          toast.error(res.msg);
        }

        // reset + close
        setShowMediaModal(false);
        setEditingMedia(false);
        setNewMedia({ role: "ICON", status: "ACTIVE" });

        // reload list
        brandDetailGet();

      } catch (e) {
        console.error(e);
        alert("Upload failed");
      } finally {
        setUploading(false);
      }
    };

    const handleDeleteMedia = async (mediaId?: number) => {
      if (!mediaId) return;
    
      // 1. Confirm first
      const confirmed = window.confirm("Are you sure you want to delete this media?");
      if (!confirmed) return;
    
      try {
        // TODO: replace with your real API
        const res = await onBrandMediaDelete({
          brandId: parseInt(brandId!),
          id: mediaId,
        });
    
        if (res.status === 0) {
          toast.success(res.msg);
        } else {
          toast.error(res.msg);
        }
    
        // Reload list
        brandDetailGet();
    
      } catch (e) {
        console.error(e);
        toast.error("Failed to delete media");
      }
    };

    const handleAttributeChange = async (attributeId: number) => {
      const attribute = attributeList.find(a => a.id === attributeId);
    
      if (!attribute) return;
    
      // set selected attribute (without choices yet)
      setNewAttribute({
        attributeId: attribute.id,
        choices: [],
      });
    
      setChoiceList([]);
    
      try {
        // TODO: replace with your real API
        const res = await onAttributeChoiceList(undefined, attributeId);
        if (res.status == 1) {
          toast.error(res.msg);
        }
    
        // available choices for UI
        setChoiceList(res.data.beans);
    
      } catch (e) {
        console.error(e);
        toast.error("Failed to load attribute choices");
      }
    };

    const removeAttributeChoiceFromCategory = async (choiceId?: number) => {
      const confirmed = window.confirm("Are you sure you want to delete this attribute choice?");
        if (!confirmed) return;

      const choicePayload : BrandAttributeChoiceEntity = {
        brandId: parseInt(brandId!),
        attributeChoiceId: choiceId
      };

      try {
        // TODO: replace with your real API
        const res = await onRemoveAttributeChoiceFromBrand(choicePayload);
        if (res.status == 0) {
          toast.success(res.msg);
        } else {
          toast.error(res.msg);
        }

        brandDetailGet();
      } catch (e) {
        console.error(e);
        toast.error("Failed to remove attribute choices");
      }
    }

    const filteredAttributes = (brandDetail?.choices || []).filter(a => {
      const attrName = a.choice?.attribute?.name?.toLowerCase() || "";
      const valueText = a.choice?.valueText?.toLowerCase() || "";
      const dataType = a.choice?.attribute?.dataType?.toLowerCase() || "";
    
      const keyword = attributeSearch.toLowerCase();
    
      return (
        attrName.includes(keyword) ||
        valueText.includes(keyword) ||
        dataType.includes(keyword)
      );
    });

    const [basicInfo, setBasicInfo] = useState<BrandEntity>({
      name: "",
      sortOrder: 0,
      status: ""
    });

    const handleSaveBasicInfo = async () => {
      try {
        const payload = {
          id: parseInt(brandId!),
          ...basicInfo,
        };
  
        // TODO: replace with your real API
        const res = await onBrandBasicUpdate(payload);
  
        if (res.status === 0) {
          toast.success(res.msg);
          brandDetailGet(); // reload from backend
        } else {
          toast.error(res.msg);
        }
  
      } catch (e) {
        console.error(e);
        toast.error("Failed to update basic information");
      }
    };

    const handleDeleteBrand = async () => {
      const confirmed = window.confirm("Delete this brand permanently?");
      if (!confirmed) return;
    
      try {
        const res = await onBrandDelete(parseInt(brandId!));
    
        if (res.status === 0) {
          toast.success(res.msg);
          navigate("/brand-list"); // go back to list
        } else {
          toast.error(res.msg);
        }
    
      } catch (e) {
        console.error(e);
        toast.error("Failed to delete brand");
      }
    };

  return (
    <Container fluid className="py-3">
      <div><Toaster position="top-right" reverseOrder={false}/></div>
      {/* ============ BASIC INFO ============ */}
      <Card className="mb-3">
        {/* <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Basic Information</span>
          <Button size="sm" onClick={handleSaveBasicInfo}>
            Save
          </Button>
        </Card.Header> */}
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Basic Information</span>

          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-danger"
              onClick={handleDeleteBrand}
            >
              <Delete fontSize="small" /> Delete
            </Button>

            <Button
              size="sm"
              variant="primary"
              onClick={handleSaveBasicInfo}
            >
              Save
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Brand Name</Form.Label>
                <Form.Control
                  value={basicInfo.name}
                  onChange={(e) =>
                    setBasicInfo(prev => ({ ...prev, name: e.target.value }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  value={basicInfo.sortOrder}
                  onChange={(e) =>
                    setBasicInfo(prev => ({ ...prev, sortOrder: Number(e.target.value) }))
                  }
                />
              </Form.Group>
              <div>Created At: {brandDetail.brand?.createdAt?.toLocaleDateString() || '-'}</div>
              <div>Updated At: {brandDetail.brand?.updatedAt?.toLocaleDateString() || '-'}</div>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={basicInfo.status}
                  onChange={(e) =>
                    setBasicInfo(prev => ({ ...prev, status: e.target.value }))
                  }
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="HIDDEN">HIDDEN</option>
                  <option value="DISABLED">DISABLED</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ============ MEDIA ============ */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between">
          <span>Brand Media ({brandDetail?.medias?.length || 0})</span>
          <Button size="sm" onClick={() => {
            setEditingMedia(false);
            setNewMedia({ role: "ICON", status: "ACTIVE" });
            setShowMediaModal(true);
          }}>
          + Add Media
        </Button>
        </Card.Header>
        <Card.Body>
          <Table bordered hover>
            <thead>
              <tr>
                <th>Preview</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {brandDetail?.medias?.map(m => (
                <tr key={m.id}>
                  <td><img src={m.preview} alt={m.role} width={80} /></td>
                  <td>{m.role}</td>
                  <td><Badge bg={m.status === 'ACTIVE' ? 'success' : 'secondary'}>{m.status}</Badge></td>
                  <td>{m.createdAt?.toLocaleDateString()}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        setEditingMedia(true);

                        setNewMedia({
                          id: m.id,
                          role: m.role,          // ✅ preload role
                          status: m.status,      // ✅ preload status
                          preview: m.preview,   // existing image
                          data: undefined,      // no new file yet
                        });

                        setShowMediaModal(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDeleteMedia(m.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* ============ ATTRIBUTES ============ */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between">
          <span>Attributes ({brandDetail?.choices?.length || 0})</span>
          <Button
            size="sm"
            onClick={async () => {
              setShowAttributeModal(true);

              // reset
              setNewAttribute(undefined);
              setChoiceList([]);

              try {
                // TODO: replace with your API
                const res = await onAttributeList();
                setAttributeList(res.data.beans);

              } catch (e) {
                console.error(e);
                alert("Failed to load attributes");
              }
            }}
          >
            + Add Attribute
          </Button>
        </Card.Header>
        <Card.Body>
          <Form.Control
            type="text"
            placeholder="Search attributes..."
            className="mb-3"
            value={attributeSearch}
            onChange={(e) => setAttributeSearch(e.target.value)}
          />
          <Table bordered hover>
            <thead>
              <tr>
                <th>Attribute</th>
                <th>Attribute Type</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredAttributes.map(a => (
                <React.Fragment key={a.attributeChoiceId}>
                  <tr>
                    <td>{a.choice?.attribute?.name}</td>
                    <td>{a.choice?.attribute?.dataType}</td>
                    <td>
                      <Row>
                        <span>
                          {a?.choice?.valueText}
                            <a onClick={() => removeAttributeChoiceFromCategory(a?.choice?.id)}
                            style={{ float: "right" }}>
                              <Delete /> 
                            </a>
                        </span> 
                      </Row>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* ============ ADD MEDIA MODAL ============ */}
      <Modal show={showMediaModal} 
        onHide={() => {
          setShowMediaModal(false);
          setNewMedia({ role: "ICON", status: "ACTIVE" });
        }} centered>
        <Modal.Header closeButton>
            <Modal.Title>
            {editingMedia ? "Edit Brand Media" : "Add Brand Media"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* ROLE */}
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={newMedia.role || "ICON"}
                onChange={(e) =>
                  setNewMedia({ ...newMedia, role: e.target.value })
                }
              >
                <option value="ICON">ICON</option>
                <option value="COVER">COVER</option>
                <option value="BANNER">BANNER</option>
              </Form.Select>
            </Form.Group>

            {/* STATUS */}
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={newMedia.status || "ACTIVE"}
                onChange={(e) =>
                  setNewMedia({ ...newMedia, status: e.target.value })
                }
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="DRAFT">DRAFT</option>
                <option value="DISABLED">DISABLED</option>
                <option value="DELETED">DELETED</option>
              </Form.Select>
            </Form.Group>

            {/* FILE */}
            <Form.Group>
              <Form.Label>File</Form.Label>
              <Form.Control
              type="file"
              accept="image/*"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  const file = files[0];

                  setNewMedia({
                    ...newMedia,
                    data: file,
                    preview: URL.createObjectURL(file),
                  });
                }
              }}
            />
            </Form.Group>

            {/* PREVIEW */}
            {newMedia.preview && (
              <div className="mt-3 text-center">
                <Badge bg="info" className="mb-2">{newMedia.role}</Badge>
                <br />
                <img
                  src={newMedia.preview}
                  alt="preview"
                  style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 6 }}
                />
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
              variant="primary"
              onClick={handleSaveMedia}
              disabled={uploading}
            >
              {uploading ? <Spinner size="sm" /> : "Upload"}
            </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ ADD ATTRIBUTE MODAL ============ */}
      <Modal
        show={showAttributeModal}
        onHide={() => setShowAttributeModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Attribute to Category</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>

            {/* ATTRIBUTE SELECT */}
            <Form.Select
              value={newAttribute?.attributeId || ""}
              onChange={(e) => handleAttributeChange(Number(e.target.value))}
            >
              <option value="">-- Select Attribute --</option>
              {attributeList.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.code})
                </option>
              ))}
            </Form.Select>

            {/* CHOICE MULTI SELECT */}
            <Form.Group>
              <Form.Label>Attribute Choices</Form.Label>
            
              <Select
                isMulti
                isDisabled={!newAttribute}
                options={choiceOptions}
                placeholder="Select attribute choices..."

                // Only show user-selected choices
                value={
                  (newAttribute?.choices || []).map(c => ({
                    value: c.attributeChoiceId!, // maps to AttributeChoiceEntity.id
                    label: c.choice?.valueText || `Choice ${c.attributeChoiceId}`,
                  }))
                }

                onChange={(selectedOptions) => {
                  const selectedIds = (selectedOptions || []).map(opt => opt.value);

                  // Map selected IDs to CategoryAttributeChoiceEntity
                  const selectedChoices: BrandAttributeChoiceEntity[] = choiceList
                  .filter(c => selectedIds.includes(c.id!))
                  .map(c => ({
                    attributeChoiceId: c.id!,
                    choice: c,
                  }));

                  setNewAttribute(prev => prev ? { ...prev, choices: selectedChoices } : prev);
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAttributeModal(false)}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={async () => {
              if (!newAttribute?.attributeId) {
                alert("Attribute is required");
                return;
              }
            
              try {
                const attributePayload = {
                  ...newAttribute,
                  brandId: parseInt(brandId!), // ensure brandId is present
                };
            
                // TODO: replace with your real API
                const res = await onBrandAttributeCreate(attributePayload);
                if (res.status === 0) {
                  toast.success(res.msg);
                  brandDetailGet(); // reload from backend
                  setShowAttributeModal(false);
                } else {
                  toast.error(res.msg);
                }
              } catch (e) {
                console.error(e);
                alert("Failed to add attribute");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BrandDetailPage;